import { DataService } from "./DataService.js";
import { CodelistService } from "./CodelistService.js";
import { EntityService } from "./EntityService.js";
import { ServiceDataManager } from "./ServiceDataManager.js";
import { AuthService } from "./AuthService.js";
import { AccountService } from "./AccountService.js";

export class ServiceFactory {

    constructor(options = {}) {

        const { httpClient, storage = null } = options;
        if (!httpClient) throw new Error('ServiceFactory: "httpClient" is required.');
        this.http = httpClient;
        this.storage = storage;

    }

    createDataService(model, options = {}) {

        const resourcePath = options.resourcePath
            ?? model?.getDataEndpointPath?.()
            ?? model?.dataEndpointPath
            ?? model?.dataEndpoint
            ?? null;
        if (!resourcePath) {
            throw new Error('ServiceFactory.createDataService: missing data endpoint.');
        }

        return new DataService({
            httpClient: this.http,
            resourcePath,
        });

    }

    createTypeCodelistValueLoader(options = {}) {

        return (type) => {
            if (!type?.valuesEndpoint)
                throw new Error('ServiceFactory.createTypeCodelistValueLoader: codelist type is missing values endpoint path.');

            return this.createCodelistService(null, {
                ...options,
                codelistPath: type.valuesEndpoint,
                storage: null,
            }).queryCodelistData();
        };

    }

    createCodelistService(model, options = {}) {

        const modelIsCodelist = model?.kind === "codelist";
        const resourcePath = options.resourcePath
            ?? model?.getDataEndpointPath?.()
            ?? model?.dataEndpointPath
            ?? model?.dataEndpoint
            ?? null;
        const codelistPath =
            options.codelistPath ??
            model?.getCodelistEndpointPath?.() ??
            model?.codelistEndpointPath ??
            model?.codelistEndpoint ??
            (modelIsCodelist ? resourcePath : null);

        return new CodelistService({
            httpClient: this.http,
            resourcePath,
            codelistPath,
            storage: options.storage ?? this.storage,
            cacheSlotName: options.cacheSlotName ?? null,
            persistent: options.persistent === true,
            expiresIn: options.expiresIn ?? 300,
            cacheKeyFactory: options.cacheKeyFactory ?? null,
        });

    }

    createEntityService(model, options = {}) {

        const dataApi = options.dataApi ?? this.createDataService(model, options);
        const codelistPath = options.codelistPath
            ?? model?.getCodelistEndpointPath?.()
            ?? model?.codelistEndpointPath
            ?? model?.codelistEndpoint
            ?? null;
        const codelistApi =
            options.codelistApi ??
            (codelistPath || model?.kind === "codelist"
                ? this.createCodelistService(model, { ...options, codelistPath })
                : null);

        return new EntityService({
            dataApi,
            codelistApi,
        });

    }

    createServiceDataManager(model, options = {}) {

        return new ServiceDataManager(
            model,
            options.entityService ?? this.createEntityService(model, options)
        );

    }

    createAuthService(options = {}) {

        const {
            loginPath = '/auth/login',
            logoutPath = '/auth/logout',
            refreshPath = '/auth/refresh',
            userPath = '/auth/user',
        } = options;

        return new AuthService({
            httpClient: this.http,
            loginPath,
            logoutPath,
            refreshPath,
            userPath,
        });

    }

    createAccountService(options = {}) {

        return new AccountService({
            httpClient: this.http,
            accountPath: options.accountPath,
        });

    }

}
